import { Injectable, inject } from '@angular/core';

import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { Observable, OperatorFunction, distinctUntilChanged, filter, map, skip, tap, withLatestFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SportsProductUtils {
    private productService = inject(SportsProductService);

    public fromSportsLeave(): Observable<boolean> {
        return this.productService.isSportsProductActive$.pipe(
            skip(1),
            distinctUntilChanged(),
            filter((x) => !x),
        );
    }

    public fromSportsEnter(): Observable<boolean> {
        return this.productService.isSportsProductActive$.pipe(
            skip(1),
            distinctUntilChanged(),
            filter((x) => x),
        );
    }

    /**
     * Emitting items if the current product is SPORTS
     */
    public filterSports<T>(): OperatorFunction<T, T> {
        return (source$: Observable<T>) =>
            source$.pipe(
                withLatestFrom(this.productService.isSportsProductActive$),
                filter(([_, isSports]) => isSports),
                map(([value]) => value),
            );
    }

    /**
     * Emitting items if the current product is SPORTS and emits the last non-SPORTS item whenever a SPORTS product is detected.
     */
    public filterSportsEmitLast<T>(): OperatorFunction<T, T> {
        let lastNotSportsValue: T | null = null;

        return (source$) =>
            new Observable((subscriber) => {
                const productSubscription = this.productService.isSportsProductActive$.pipe(filter((x) => x)).subscribe(() => {
                    if (lastNotSportsValue) {
                        subscriber.next(lastNotSportsValue);
                        lastNotSportsValue = null;
                    }
                });

                const sourceSubscription = source$
                    .pipe(
                        withLatestFrom(this.productService.isSportsProductActive$),
                        tap(([value, isSports]) => {
                            lastNotSportsValue = isSports ? null : value;
                        }),
                        filter(([_, isSports]) => isSports),
                        map(([value]) => value),
                    )
                    .subscribe({
                        next: (value) => {
                            subscriber.next(value);
                        },
                        error: (err: unknown) => {
                            subscriber.error(err);
                        },
                        complete: () => {
                            subscriber.complete();
                        },
                    });

                return () => {
                    productSubscription.unsubscribe();
                    sourceSubscription.unsubscribe();
                };
            });
    }
}
